import { Controller } from 'core/Controller';
import { Events } from 'core/events/Events';
import { AppEvents } from 'app/events/AppEvents';
import { MenuStateType, AboutSectionType, DepartmentType } from 'app/data/Enums';
import { AboutController } from 'app/views/about/AboutController';
import { HistoryStateModel } from 'app/model/HistoryStateModel';
import { MouseController } from 'app/chrome/mouse/MouseController';
import Config from 'config/settings';
import { Simple } from 'utils/analytics';
import { UIController } from 'app/views/ui/UIController';

import { NavMenuView } from './NavMenuView';
import { NavMenuAboutFlow } from './NavMenuAboutFlow';

/**
 * Overlay nav menu controller. Orchestrates the per-concern sub-objects in
 * this folder (see navmenu.types.ts for the decomposition pattern,
 * established in src/app/views/work/). Keeps: the lifecycle hooks, the full
 * public surface used by AppController / AboutController / UIController /
 * ProjectsMenuController / WorkController, every Events.on(...) registration,
 * and all cross-controller access (this.get(...) / injected controllers).
 */
export class NavMenuController extends Controller {
	private _aboutController: AboutController;
	private _mouseController: MouseController;
	private _uiController: UIController;

	private _state: HistoryStateModel;
	private _openingState: HistoryStateModel;
	private _openAtState: HistoryStateModel;

	private _flagNoHistoryStateOff: boolean = false;
	private _hasOpenHistoryState: boolean = false;
	private _isDeeplink: boolean = false;
	private _menuState: MenuStateType = MenuStateType.CLOSED;

	private _delayInteractionInt;
	private _allowInteraction: boolean = true;

	//Per-concern sub-objects (see navmenu.types.ts for the pattern):
	private _view: NavMenuView;
	private _aboutFlow: NavMenuAboutFlow;

	constructor(id: string) {
		super(id);

		this._view = new NavMenuView(this.DOM);

		this._aboutFlow = new NavMenuAboutFlow({
			getContainer: () => this.container,
			isDeeplink: () => this._isDeeplink,
			getState: () => this._state,
			getOpeningState: () => this._openingState,
			getFlagNoHistoryStateOff: () => this._flagNoHistoryStateOff,
			setFlagNoHistoryStateOff: (value: boolean) => (this._flagNoHistoryStateOff = value),
			clickMenuButton: () => this._view.$menuButton.click(),
			getContentEl: () => this._view.$content,
			setMenuSelectedState: () => this._view.setMenuSelectedState(),
			onAllowAboutScroll: () => this._view.allowAboutScroll(),
			onBlockAboutScroll: () => this._view.blockAboutScroll(),
			aboutNavMenuPressItemPress: (index: number) => this._aboutController.onNavMenuPressItemPress(index),
			getAboutSubsectionTitle: (section: AboutSectionType) => this._aboutController.getSubsectionTitle(section),
			aboutBackToMenuMainView: () => this._aboutController.backToMenuMainView(),
			//KNOWN PRE-EXISTING BUG (tracked separately — preserved, NOT fixed):
			//aboutSectionActive() does not exist on ProjectController. Moved verbatim
			//from the old _onMenuInfoLinkClick (now NavMenuAboutFlow.onMenuInfoLinkClick).
			projectAboutSectionActive: () => this.get('project').aboutSectionActive(),
			isHomeOnDepartment: (department: DepartmentType) => this.get('home').isOnDepartment(department),
			triggerHomeExplore: () => this.get('home').triggerExplore(),
			setMouseDragCursorActive: (active: boolean) => this.get('Mouse').setDragCursorActive(active),
			setScrollbarActive: (active: boolean) => this.get('Scrollbar').setActive(active)
		});
	}

	public init(): void {}

	//** Set controller depencies */
	public setControllers(about: AboutController, mouse: MouseController, uiController: UIController): void {
		this._aboutController = about;
		this._mouseController = mouse;
		this._uiController = uiController;
	}

	public delayInfoButtonInteraction() {
		console.log('delayInfoButtonInteraction');
		this._allowInteraction = false;
		clearTimeout(this._delayInteractionInt);
		this._delayInteractionInt = setTimeout(() => {
			this._allowInteraction = true;
		}, 700);
	}

	public DOMReady(): void {
		if (!this.container) {
			return;
		}

		this._view.queryRefs(this.container);

		Events.on('click', '.ui__hamburger.-main', (e: MouseEvent) => {
			e.preventDefault();
			this._onMenuButtonClick(e);
			return false;
		});

		Events.on('click', '.navmenu__link.-departmentlink', (e: MouseEvent) => {
			this._view.exitLink(e);
		});

		Events.on('click', '.show__info__button', (e: MouseEvent) => {
			e.preventDefault();

			if (this._menuState == MenuStateType.CLOSED) {
				let state: MenuStateType = MenuStateType.OPEN;
				this._setMenuState(state);
				Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });

				return false;
			}

			return false;
			let state: MenuStateType = MenuStateType.CLOSED;
			this._setMenuState(state);
			Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });

			return false;
		});

		Events.on('click', '.ui_close__button', (e: MouseEvent) => {
			e.preventDefault();
			if (this._menuState == MenuStateType.CLOSED) {
				return;
			}

			console.log('CLOSE MENU STATE--------------------');
			this.delayInfoButtonInteraction();
			let state: MenuStateType = MenuStateType.CLOSED;
			this._setMenuState(state);
			Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });

			return false;
		});

		if (Config.doInfoButtonMouseOver) {
			Events.on('mouseover', '.show__info__button', (e: MouseEvent) => {
				if (
					!this._allowInteraction ||
					this._menuState != MenuStateType.CLOSED ||
					this._mouseController.lastPointerWasTouch()
				) {
					return;
				}

				let state: MenuStateType = MenuStateType.OPEN;
				this._setMenuState(state);
				Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });

				return false;
			});
		}

		Events.on('mouseover', '.navmenu__hide__area', (e: MouseEvent) => {
			e.preventDefault();
			if (this._menuState == MenuStateType.CLOSED) {
				return;
			}

			this.delayInfoButtonInteraction();
			let state: MenuStateType = MenuStateType.CLOSED;
			this._setMenuState(state);
			Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });

			return false;

			this._setMenuState(state);
			Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });
			return false;
		});

		Events.on('mouseover', '.navmenu__link.-departmentlink', (e: MouseEvent) => {
			(e.target as HTMLElement).querySelector('video').play();
		});

		Events.on('mouseout', '.navmenu__link.-departmentlink', (e: MouseEvent) => {
			(e.target as HTMLElement).querySelector('video').pause();
		});

		this._bindInfoLinksEvents();

		Events.on('click', '.navmenu__back', (e: MouseEvent) => {
			e.preventDefault();
			if (this._menuState == MenuStateType.CLOSED) {
				return;
			}

			this.delayInfoButtonInteraction();
			let state: MenuStateType = MenuStateType.CLOSED;
			this._setMenuState(state);
			Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });

			return false;

			//NOTE: dead (unreachable) tail moved intact — see NavMenuAboutFlow.onBackToMainView
			e.preventDefault();
			this._aboutFlow.onBackToMainView(e);
			return false;
		});

		this._view.setMenuSelectedState(this._state);
	}

	public initForAboutDeeplink(intialState: HistoryStateModel) {
		this._isDeeplink = true;
		this._openingState = intialState;
		this._bindInfoLinksEvents();
	}

	public showForAboutDeepLink() {
		gsap.TweenMax.to(this.container, 0.5, { ease: Config.linearEase, autoAlpha: 1 });
	}

	/** Is showing */
	public isShowing(): boolean {
		return this.DOM.hasClass(this.container, '-open');
	}

	/** Section has change so set state */
	public setState(state: HistoryStateModel, updateMenuState: boolean = true): void {
		this._state = state;
		if (updateMenuState) {
			this._view.setMenuSelectedState(state);
		}
	}

	/** Reset the overlay nav menu after the transition to */
	public reset(): void {}

	public resize(): void {
		//Resize the BG so that it leaves 80px area on left (If info view is active):

		this._aboutFlow.handleResize();
	}

	public historyBackToRichSection() {
		return;
		this._flagNoHistoryStateOff = true;
		this._view.$menuButton.click();
	}

	public historyBackToMenu() {
		this._flagNoHistoryStateOff = true;
		this._view.$backButton.click();
	}

	public historyBackToInfoFromMenu() {
		this._aboutFlow.infoSectionActive = true;
		this._aboutFlow.transitionToInfo();
		(this.get('about') as AboutController).show();
	}

	public historyFromRichToMenu(state: HistoryStateModel) {
		//OPEN WITH ABOUT/INFO/CONTACT checked
		this._openAtState = state;

		this._flagNoHistoryStateOff = true;
		this._view.$menuButton.click();
	}

	public debugLogoPress(): boolean {
		if (this._menuState === MenuStateType.OPEN) {
			return this._aboutFlow.processHomeClick(AboutSectionType.HOME);
		}

		return false;
	}

	public leaveMenu(fromHistoryState = false) {
		Simple.event('infomenu_close');

		this._menuState = MenuStateType.CLOSED;
		this._aboutController.resetSection();

		this._delayInfoButtonShow();

		let delay = 0;

		setTimeout(() => {
			if (!fromHistoryState && this._hasOpenHistoryState && this._aboutFlow.infoSectionActive) {
				Events.dispatch(AppEvents.CHANGE_SECTION, {
					section: this._openingState.section,
					subsection: this._openingState.subsection || null,
					projectSlug: this._openingState.projectSlug || null,
					href: this._openingState.href,
					title: this._openingState.title,
					closeMenuAction: true,
					skipHistoryState: this._flagNoHistoryStateOff,
					hideMenu: true
				});
			}

			this._view.animateOutMenu();

			this._aboutController.backToParallax();

			this._aboutFlow.infoSectionActive = false;

			this.get('Mouse').setDragCursorActive(true);

			setTimeout(() => {
				this.DOM.removeClass(document.querySelector('body'), '-navmenuopen');
				this.DOM.removeClass(document.querySelector('html'), '-navmenuopen');
			}, 20);
		}, delay);

		this.get('Pixi').unpause();
	}

	//PRIVATE
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------
	//----------------------------------------------------------------------------

	private _bindInfoLinksEvents(): void {
		Events.on('click', '.navmenu__link.-infolink', (e: MouseEvent) => {
			e.preventDefault();
			let shouldCanceDefault = this._aboutFlow.onMenuInfoLinkClick(e);

			return false;
			if (shouldCanceDefault) {
				e.preventDefault();
				return false;
			}

			this._view.exitLink(e);
			return true;
		});
	}

	private _addOpenHistoryState() {

		this._hasOpenHistoryState = false;

		if (!this._flagNoHistoryStateOff) {
			this._hasOpenHistoryState = true;
		}
	}

	/** Hamburger menu  press */
	private _onMenuButtonClick(e: MouseEvent): void {
		let $hamburger: Element = e.target as Element;
		let state: MenuStateType = this.DOM.hasClass($hamburger, '-open') ? MenuStateType.CLOSED : MenuStateType.OPEN;
		this._setMenuState(state);

		//Dispatch event so active controllers can be 'paused'
		Events.dispatch(AppEvents.MENU_STATE_CHANGE, { state: state, hasFlag: this._flagNoHistoryStateOff });
	}

	private _delayInfoShowTimeout = null;

	private _delayInfoButtonShow() {
		this._resetInfoButtonShow();

		this._uiController.hideUiItem('info');

		this._delayInfoShowTimeout = setTimeout(() => {
			this._uiController.showUiItem('info');
			this._delayInfoShowTimeout = null;
		}, 700);
	}

	private _resetInfoButtonShow() {
		if (this._delayInfoShowTimeout) {
			clearTimeout(this._delayInfoShowTimeout);
			this._delayInfoShowTimeout = null;
		}
	}

	/** Set Menu state */
	private _setMenuState(state: MenuStateType): void {
		this._menuState = state;

		this._view.reset();

		//Toogle state from open/close
		if (state === MenuStateType.CLOSED) {
			this.leaveMenu();
		} else {
			//Opening the menu, so record the open state so cango back on close:

			Simple.event('infomenu_open');
			this._aboutFlow.recordInnerWidth();

			this._openingState = this._state;

			(this.DOM.addClass(document.querySelector('body'), '-navmenuopen'),
				this.DOM.addClass(document.querySelector('html'), '-navmenuopen'));
			this.get('project').matchContentYPosition();

			this.get('Pixi').pause();

			this._resetInfoButtonShow();
			this._aboutController.resetScrollProgress();

			if (this._openAtState) {
				this._aboutFlow.infoSectionActive = true;
				this._aboutController.forceSection(this._openAtState.subsection as AboutSectionType);
				this._setMenuToAboutState();
				this._view.setMenuSelectedState(this._openAtState);
				this._aboutFlow.transitionToInfo();

				this._state = this._openAtState;
				this._openAtState = null;

				this._view.delayCloseButtonShow();
			} else {
				this._aboutFlow.infoSectionActive = false;
				this._view.resetMenu();

				this._view.prepareAnimateMenu();
				this._view.animateInMenu();

				this._addOpenHistoryState();
				this._view.openLinksState(this._openingState);
			}
		}

		this._flagNoHistoryStateOff = false;
	}

	private _setMenuToAboutState() {
		this._view.setMenuToAboutState();

		this._aboutController.show(null, false);
		this._aboutController.gotoAboutSubsetion(this._openAtState.subsection as AboutSectionType);

		this._view.showMenuContent(false);
	}
}
